import { ReactNode, useState } from 'react';
import { useMyDataCompanyInfo, usePosConnectors } from '@/features/aade-mydata/aade-mydata.queries';
import { getSettingsOptions } from '@/features/settings/settings.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { format, parse, parseISO } from 'date-fns';
import {
  ArrowLeft,
  Building2,
  CalendarRange,
  FileText,
  Mail,
  MapPin,
  Phone,
  PlugZap,
} from 'lucide-react';

import { MyDataCompanyInfo } from '@/types/aade-mydata/aade-mydata.types';
import { Access } from '@/config/access.config';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { checkAccess } from '@/components/guards/check-access';

import CreatePosConnectorModal from './-create-pos-connector-modal';
import PosConnectorsTab from './-pos-connector-tab';

export const Route = createFileRoute('/_app/(aade-mydata)/aade-mydata/settings')({
  component: MyDataSettingsPage,
  beforeLoad: ({ context }) => {
    const settings = context.queryClient.getQueryData(getSettingsOptions.queryKey);
    const userData = context.queryClient.getQueryData(getUserOptions.queryKey);

    if (!checkAccess(settings, userData?.user, Access.AadeMyData)) {
      throw redirect({ to: '/' });
    }
  },
});

function MyDataSettingsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyDataCompanyInfo();
  const { data: connectors = [], isLoading: isLoadingConnectors } = usePosConnectors();
  const [activeTab, setActiveTab] = useState<'company' | 'connectors'>('company');
  const [isPosModalOpen, setIsPosModalOpen] = useState(false);

  if (isLoading) {
    return <div className='p-6'>Loading company info...</div>;
  }

  if (isError || !data) {
    return <div className='p-6 text-red-600'>Αποτυχία φόρτωσης στοιχείων εταιρείας.</div>;
  }

  return (
    <div className='min-h-full bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.16),_transparent_28%),linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,1))] p-4 md:p-6'>
      <div className='mx-auto flex max-w-7xl flex-col gap-6'>
        <div className='overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-[0_24px_80px_-32px_rgba(15,23,42,0.35)] backdrop-blur'>
          <div className='flex flex-col gap-6 border-b border-slate-100 bg-[linear-gradient(135deg,rgba(14,116,144,0.08),rgba(59,130,246,0.14),rgba(255,255,255,0.9))] p-6 md:p-8'>
            <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
              <div className='space-y-4'>
                <Button
                  variant='outline'
                  className='bg-white/80'
                  onClick={() => navigate({ to: '/aade-mydata' })}
                >
                  <ArrowLeft className='h-4 w-4' />
                  Επιστροφή
                </Button>

                <div className='space-y-3'>
                  <div className='flex flex-wrap items-center gap-3'>
                    <Badge className='bg-slate-900 text-white hover:bg-slate-900'>
                      AADE / MyData Settings
                    </Badge>
                    <Badge
                      className={
                        data.isActive
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-red-200 bg-red-50 text-red-700'
                      }
                      variant='outline'
                    >
                      {data.isActive ? 'Ενεργό προφίλ' : 'Ανενεργό προφίλ'}
                    </Badge>
                  </div>

                  <div>
                    <h1 className='text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl'>
                      {data.companyName || 'Στοιχεία εταιρείας'}
                    </h1>
                    <p className='mt-2 max-w-3xl text-sm leading-6 text-slate-600 md:text-base'>
                      Κεντρική προβολή για στοιχεία εταιρείας, συνδρομή και όρια παραστατικών, με
                      καθαρή παρουσίαση και χωρίς ευαίσθητα credentials.
                    </p>
                  </div>
                </div>
              </div>

              <div className='grid min-w-full grid-cols-2 gap-3 md:min-w-[320px]'>
                <MetricTile
                  icon={<FileText className='h-4 w-4' />}
                  label='Καταναλωμένα'
                  value={formatNumber(data.invoicesConsumed)}
                  tone='blue'
                />
                <MetricTile
                  icon={<CalendarRange className='h-4 w-4' />}
                  label='Υπόλοιπο μήνα'
                  value={formatNumber(data.monthRemainingInvoices)}
                  tone='emerald'
                />
                <MetricTile
                  icon={<Building2 className='h-4 w-4' />}
                  label='ΑΦΜ'
                  value={data.companyVatNumber || '-'}
                  tone='slate'
                />
                <MetricTile
                  icon={<CalendarRange className='h-4 w-4' />}
                  label='Λήξη συνδρομής'
                  value={formatDate(data.endDate, 'dd/MM/yyyy')}
                  tone='amber'
                />
              </div>
            </div>
          </div>
        </div>

        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          <MiniStat
            icon={<FileText className='h-4 w-4' />}
            label='Μηνιαίο όριο'
            value={formatNumber(data.monthlyInvoices)}
          />
          <MiniStat
            icon={<FileText className='h-4 w-4' />}
            label='Όριο ταμειακής'
            value={formatNumber(data.cashRegisterMonthlyInvoices)}
          />
          <MiniStat
            icon={<Mail className='h-4 w-4' />}
            label='Email'
            value={data.companyEmail || '-'}
          />
          <MiniStat
            icon={<Phone className='h-4 w-4' />}
            label='Τηλέφωνο'
            value={data.companyPhoneNumber || data.companyMobilePhoneNumber || '-'}
          />
        </div>

        <div className='flex flex-wrap gap-3 rounded-[24px] border border-slate-200 bg-white/90 p-3 shadow-[0_16px_50px_-28px_rgba(15,23,42,0.22)]'>
          <TabButton
            isActive={activeTab === 'company'}
            icon={<Building2 className='h-4 w-4' />}
            label='Στοιχεία εταιρείας'
            onClick={() => setActiveTab('company')}
          />
          <TabButton
            isActive={activeTab === 'connectors'}
            icon={<PlugZap className='h-4 w-4' />}
            label='POS Connectors'
            onClick={() => setActiveTab('connectors')}
          />
        </div>

        {activeTab === 'company' && (
          <div className='grid gap-6 xl:grid-cols-[1.1fr_0.9fr]'>
            <div className='grid gap-6'>
              <InfoCard
                title='Συνδρομή'
                description='Βασικά στοιχεία ενεργοποίησης και πακέτου.'
                icon={<CalendarRange className='h-4 w-4' />}
                items={[
                  { label: 'Προϊόν', value: data.productName },
                  { label: 'Έκδοση', value: data.wpEdition },
                  { label: 'Ημερομηνία αγοράς', value: formatDate(data.saleDate) },
                  { label: 'Ημερομηνία ενεργοποίησης', value: formatDate(data.activationDate) },
                  { label: 'Ημερομηνία λήξης', value: formatDate(data.endDate) },
                  {
                    label: 'Διάρκεια myDATA',
                    value: data.myDataDuration ? `${data.myDataDuration} ημέρες` : '-',
                  },
                ]}
              />

              <InfoCard
                title='Εταιρεία'
                description='Μητρώο και βασικά φορολογικά στοιχεία.'
                icon={<Building2 className='h-4 w-4' />}
                items={[
                  { label: 'Επωνυμία', value: data.companyName },
                  { label: 'Διακριτικός τίτλος', value: data.companySmallName },
                  { label: 'ΑΦΜ', value: data.companyVatNumber },
                  { label: 'ΔΟΥ', value: data.companyDoy },
                  { label: 'Δραστηριότητα', value: data.companyActivity },
                ]}
              />
            </div>

            <div className='grid gap-6'>
              <InfoCard
                title='Όρια παραστατικών'
                description='Τρέχοντα διαθέσιμα και μηνιαία όρια.'
                icon={<FileText className='h-4 w-4' />}
                items={[
                  {
                    label: 'Μηνιαίο όριο παραστατικών',
                    value: formatNumber(data.monthlyInvoices),
                  },
                  {
                    label: 'Καταναλωμένα παραστατικά',
                    value: formatNumber(data.invoicesConsumed),
                  },
                  {
                    label: 'Υπόλοιπο παραστατικών μήνα',
                    value: formatNumber(data.monthRemainingInvoices),
                  },
                  {
                    label: 'Υπόλοιπο ταμειακής μήνα',
                    value: formatNumber(data.cashRegisterMonthRemainingInvoices),
                  },
                  {
                    label: 'Μηνιαίο όριο ταμειακής',
                    value: formatNumber(data.cashRegisterMonthlyInvoices),
                  },
                ]}
              />

              <InfoCard
                title='Επικοινωνία'
                description='Στοιχεία έδρας και επικοινωνίας.'
                icon={<MapPin className='h-4 w-4' />}
                items={[
                  { label: 'Διεύθυνση', value: data.companyAddress },
                  { label: 'Πόλη', value: data.companyCity },
                  { label: 'ΤΚ', value: data.companyTk },
                  { label: 'Email', value: data.companyEmail },
                  { label: 'Τηλέφωνο', value: data.companyPhoneNumber },
                  { label: 'Κινητό', value: data.companyMobilePhoneNumber },
                ]}
              />

              <InfoCard
                title='Νόμιμος εκπρόσωπος'
                description='Προβάλλονται μόνο τα μη ευαίσθητα στοιχεία.'
                icon={<Building2 className='h-4 w-4' />}
                items={[
                  {
                    label: 'Ονοματεπώνυμο',
                    value: getRepresentativeFullName(data),
                  },
                ]}
              />
            </div>
          </div>
        )}

        {activeTab === 'connectors' && (
          <PosConnectorsTab
            connectors={connectors}
            onCreate={() => setIsPosModalOpen(true)}
            isLoading={isLoadingConnectors}
          />
        )}
      </div>

      <CreatePosConnectorModal open={isPosModalOpen} onClose={() => setIsPosModalOpen(false)} />
    </div>
  );
}

function InfoCard({
  title,
  description,
  icon,
  items,
}: {
  title: string;
  description: string;
  icon: ReactNode;
  items: Array<{ label: string; value: string | null | undefined }>;
}) {
  return (
    <Card className='overflow-hidden border-slate-200 bg-white/90 shadow-[0_16px_50px_-28px_rgba(15,23,42,0.35)]'>
      <CardHeader className='border-b border-slate-100 bg-slate-50/80'>
        <div className='flex items-center gap-3'>
          <div className='flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white'>
            {icon}
          </div>
          <div>
            <CardTitle className='text-base text-slate-950'>{title}</CardTitle>
            <CardDescription className='mt-1'>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-3 p-4 md:p-6'>
        {items.map((item) => (
          <div
            key={item.label}
            className='grid gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-3 md:grid-cols-[190px_1fr]'
          >
            <span className='text-sm font-medium text-muted-foreground'>{item.label}</span>
            <span className='break-words text-sm font-medium text-slate-900'>
              {item.value || '-'}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function MetricTile({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  tone: 'blue' | 'emerald' | 'slate' | 'amber';
}) {
  const toneClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };

  return (
    <div className='rounded-3xl border border-white/70 bg-white/85 p-4 shadow-sm backdrop-blur'>
      <div
        className={`mb-4 flex h-9 w-9 items-center justify-center rounded-2xl border ${toneClasses[tone]}`}
      >
        {icon}
      </div>
      <div className='text-xs font-medium uppercase tracking-[0.16em] text-slate-500'>{label}</div>
      <div className='mt-2 break-words text-lg font-semibold text-slate-950'>{value}</div>
    </div>
  );
}

function MiniStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className='rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-[0_14px_40px_-30px_rgba(15,23,42,0.35)]'>
      <div className='flex items-center gap-2 text-slate-500'>
        <div className='flex h-8 w-8 items-center justify-center rounded-2xl bg-slate-100'>
          {icon}
        </div>
        <span className='text-sm font-medium'>{label}</span>
      </div>
      <div className='mt-3 break-words text-base font-semibold text-slate-900'>{value}</div>
    </div>
  );
}

function TabButton({
  isActive,
  icon,
  label,
  onClick,
}: {
  isActive: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium transition ${
        isActive
          ? 'bg-slate-900 text-white shadow-sm'
          : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function getRepresentativeFullName(data: MyDataCompanyInfo) {
  return [data.representativeName, data.representativeSurname].filter(Boolean).join(' ') || '-';
}

function formatDate(value: string | null, outputFormat = 'dd/MM/yyyy HH:mm') {
  if (!value) {
    return '-';
  }

  try {
    if (value.includes('T')) {
      return format(parseISO(value), outputFormat);
    }

    return format(parse(value, 'yyyy-MM-dd HH:mm:ss.S', new Date()), outputFormat);
  } catch {
    return value;
  }
}

function formatNumber(value: number | null) {
  if (value === null || value === undefined) {
    return '-';
  }

  return value.toString();
}
